import { useState } from 'react'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { FormItem, Form } from '@/components/ui/Form'
import PasswordInput from '@/components/shared/PasswordInput'
import classNames from '@/utils/classNames'
import { useAuth } from '@/auth'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { ZodType } from 'zod'
import type { CommonProps } from '@/@types/common'
import type { ReactNode } from 'react'
import { Checkbox } from '@/components/ui';
import CaptchaField from '@/components/form/fields/CaptchaField'

interface SignInFormProps extends CommonProps {
    disableSubmit?: boolean
    passwordHint?: string | ReactNode
    setMessage?: (message: string) => void
}

type SignInFormSchema = {
    email: string
    password: string
    remember?: boolean
    recaptcha?: string | null
}

const validationSchema: ZodType<SignInFormSchema> = z.object({
    email: z
        .string({ required_error: 'Please enter your email' })
        .min(1, { message: 'Please enter your email' }),
    password: z
        .string({ required_error: 'Please enter your password' })
        .min(1, { message: 'Please enter your password' }),
    remember: z.boolean().optional(),
    recaptcha: z.string().optional().nullable(),
})

const SignInForm = (props: SignInFormProps) => {
    const [isSubmitting, setSubmitting] = useState<boolean>(false)
    const [captchaKey, setCaptchaKey] = useState(0)

    const { disableSubmit = false, className, setMessage, passwordHint } = props

    const {
        handleSubmit,
        formState: { errors },
        control,
        resetField,
    } = useForm<SignInFormSchema>({
        defaultValues: {
            email: '',
            password: '',
            remember: true,
            recaptcha: undefined,
        },
        resolver: zodResolver(validationSchema),
    })

    const { signIn } = useAuth()

    const resetCaptcha = () => {
        resetField('recaptcha')
        setCaptchaKey((key) => key + 1)
    }

    const onSignIn = async (values: SignInFormSchema) => {
        const { email, password, remember = false } = values

        if (!disableSubmit) {
            setSubmitting(true)

            const result = await signIn({ email, password, remember, recaptcha: values['recaptcha'] })

            if (result?.status === 'failed') {
                setMessage?.(result.message)
                resetCaptcha()
            }
        }

        setSubmitting(false)
    }

    return (
        <div className={className}>
            <Form onSubmit={handleSubmit(onSignIn, resetCaptcha)}>
                <FormItem
                    label="Email"
                    invalid={Boolean(errors.email)}
                    errorMessage={errors.email?.message}
                >
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="email"
                                placeholder="Email"
                                autoComplete="off"
                                {...field}
                            />
                        )}
                    />
                </FormItem>
                <FormItem
                    label="Password"
                    invalid={Boolean(errors.password)}
                    errorMessage={errors.password?.message}
                    className={classNames(
                        passwordHint ? 'mb-0' : '',
                        errors.password?.message ? 'mb-8' : '',
                    )}
                >
                    <Controller
                        name="password"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <PasswordInput
                                type="text"
                                className="mb-4"
                                placeholder="Password"
                                autoComplete="off"
                                {...field}
                            />
                        )}
                    />
                </FormItem>

                {/* Remember me checkbox */}
                <Controller
                    name="remember"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                          defaultChecked
                          {...field}
                        >
                            Remember me
                        </Checkbox>
                    )}
                />

                {/* Captcha */}
                <CaptchaField
                    key={captchaKey}
                    name="recaptcha"
                    control={control}
                    className="my-4"
                />

                {passwordHint}
                <Button
                    block
                    loading={isSubmitting}
                    variant="solid"
                    type="submit"
                >
                    {isSubmitting ? 'Signing in...' : 'Sign In'}
                </Button>
            </Form>
        </div>
    )
}

export default SignInForm
